/**
 * Site shell for every logged-in page.
 *
 * Flex-column layout that pins the Footer to the bottom even when the
 * page content is short. Header is sticky; content fills the rest of
 * the viewport.
 *
 *   ┌─────────────────┐
 *   │     Header      │  sticky
 *   ├─────────────────┤
 *   │                 │
 *   │   {children}    │  flex: 1
 *   │                 │
 *   ├─────────────────┤
 *   │     Footer      │  mt: auto
 *   └─────────────────┘
 *
 * Auth pages (login/signup/etc.) are excluded — they use AuthLayout
 * instead, which has its own footer baked in.
 */

import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import { ActingAsBanner } from './ActingAsBanner';
import { DesktopRecommendationHint } from './DesktopRecommendationHint';
import { Footer } from './Footer';
import { Header } from './Header';

/**
 * Site-wide page surface colour. Warm ivory matches the auth pages
 * and the Profile / Change-password settings, giving every logged-in
 * surface the same canvas. Pages can override via the `bgcolor` prop
 * if they really need to (rare; one-off pages may want a darker hero).
 */
export const SITE_BG = '#faf6ee';

interface SiteLayoutProps {
  /** Optional subtitle shown next to the brand mark in the Header. */
  subtitle?: string;
  /** Whether the Header should render the "Home" button. */
  showHome?: boolean;
  /** Override the page surface colour. Defaults to brand ivory. */
  bgcolor?: string;
  /**
   * Opt-in: render a small dismissible "open on desktop for the full
   * view" hint on xs/sm viewports.  Use on analytics-dense pages
   * (Sales, Marketing, Admin); skip on simple form pages (Profile,
   * change password) where there's no richer desktop view.
   */
  showDesktopHint?: boolean;
  /**
   * App-shell mode: pin the layout to exactly the viewport height so
   * the document never scrolls, and the page's own content scrolls
   * internally instead.
   *
   * Why this exists: the default `minHeight: 100vh` is only a MINIMUM,
   * so the shell grows to fit its content. A `flex: 1` child therefore
   * gets grow but never shrink — there's no definite height to
   * distribute — and a tall child (a chat transcript, say) pushes the
   * footer below the fold. Setting a definite height gives flex
   * something to divide, so the footer stays put.
   *
   * Only for pages that manage their own internal scrolling (Notes).
   * A normal document-flow page must NOT set this, or its content will
   * be clipped instead of scrolling.
   */
  fitViewport?: boolean;
  children: ReactNode;
}

export function SiteLayout({
  subtitle,
  showHome,
  bgcolor,
  showDesktopHint = false,
  fitViewport = false,
  children,
}: SiteLayoutProps) {
  return (
    <Box
      sx={{
        // On mobile Safari / Chrome, `100vh` is the EXPANDED viewport —
        // the height you get once the URL bar retracts. Sizing the
        // shell to it means the footer sits under the browser chrome
        // until you scroll. `100dvh` tracks the actually-visible
        // height; the `vh` values stay as the fallback.
        ...(fitViewport
          ? {
              height: '100vh',
              '@supports (height: 100dvh)': { height: '100dvh' },
              // The page scrolls internally; without this the document
              // gets a second scrollbar for the overflow.
              overflow: 'hidden',
            }
          : {
              minHeight: '100vh',
              '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
            }),
        display: 'flex',
        flexDirection: 'column',
        bgcolor: bgcolor ?? SITE_BG,
      }}
    >
      <Header subtitle={subtitle} showHome={showHome} />
      <ActingAsBanner />
      {showDesktopHint && <DesktopRecommendationHint />}
      {/* `minHeight: 0` lets this actually shrink. A flex item's default
          `min-height: auto` refuses to go below its content, which would
          push the Footer off-screen no matter what the child does. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
      <Footer />
    </Box>
  );
}
